'use client';
import cn from 'classnames';

import Button from '@/components/common/Button/Button';
import Text from '@/components/common/Text/Text';

import './BuilderCaptureModal.style.scss';
import { FC, useState } from 'react';
import BaseModal from '@/components/modal/lib/BaseModal';
import EmailSmsModalForm from '@/components/form/EmailSmsModalForm/EmailSmsModalForm';

export interface BuilderCaptureModalCMSContent {
  /** Text content to display in the modal on desktop */
  modalTextDesktop: string;

  /** Text content to display in the modal on mobile */
  modalTextMobile: string;

  /** Header content to display in the modal on desktop */
  modalHeaderDesktop: string;

  /** Header content to display in the modal on mobile */
  modalHeaderMobile: string;

  /** Button text for the call-to-action on desktop */
  buttonText?: string;

  /** Target for the button to link to */
  buttonTarget?: string;

  /** Button color */
  buttonColor?: string;

  /** Whether to open the link in a new tab */
  openInNewTab?: boolean;

  /** Text color for the modal other than buttons */
  textColor?: string;

  /** Promo code to provide the user after joining the mailing list */
  promoCode?: string;

  /** Description of the promo code */
  promoCodeDescription?: string;

  /** Background color for the promo code screen */
  backgroundColor?: string;

  /** Whether to show the modal in the app */
  showInApp?: boolean;

  /** How often to show the modal in days */
  displayFrequency?: number;

  /** How long to delay the modal in seconds */
  initialDelay?: number;

  /** Optional CSS class name */
  className?: string;

  enabled: boolean;
}

export interface BuilderCaptureModalProps {
  cmsContent?: BuilderCaptureModalCMSContent;

  /** Function to call when the modal is dismissed */
  handleDismiss?: () => void;

  /** Function to call when the form is submitted successfully */
  onFormSuccess?: () => void;
}

const BuilderCaptureModal: FC<BuilderCaptureModalProps> = ({
  cmsContent,
  handleDismiss,
  onFormSuccess
}) => {
  const {
    modalTextDesktop,
    modalTextMobile,
    modalHeaderDesktop,
    modalHeaderMobile,
    buttonText,
    buttonTarget = '',
    buttonColor = '#121212',
    openInNewTab = false,
    textColor = '#121212', // Default dark text
    className = '',
    enabled,
    promoCode,
    promoCodeDescription,
    backgroundColor
  } = cmsContent || {};
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(false);

  // Determine if there is any content to show at all (either mobile or desktop)
  const hasAnyContent = Boolean(
    modalTextDesktop ||
      modalHeaderDesktop ||
      buttonText ||
      modalTextMobile ||
      modalHeaderMobile ||
      buttonText
  );

  // Check to see if the component is disabled
  if (!enabled || !handleDismiss || !hasAnyContent) {
    return null;
  }

  if (isSubmittedSuccessfully && promoCode) {
    return (
      <BaseModal
        className={cn('BuilderCaptureModal', className)}
        onClose={handleDismiss}
      >
        <div
          className='BuilderCaptureModal-content'
          style={{ backgroundColor }}
        >
          <div className='BuilderCaptureModal-promo-code-container'>
            <div className='BuilderCaptureModal-header-container'>
              <Text
                variant='title-xl'
                italic
                style={{ color: textColor }}
                className={cn('BuilderCaptureModal-header')}
              >
                You&apos;re subscribed!
              </Text>
            </div>
            <div className='BuilderCaptureModal-text-container'>
              <Text
                variant='text-m'
                style={{ color: textColor }}
                className={cn('BuilderCaptureModal-text')}
              >
                Use code{' '}
                <Text
                  variant='text-m'
                  style={{ fontWeight: 'bold' }}
                >
                  {promoCode}
                </Text>
                {promoCodeDescription}
              </Text>
            </div>
            <div className='BuilderCaptureModal-button-container'>
              <Button
                variant='dark'
                className='Button small BuilderCaptureModal-button'
                onClick={(e) => {
                  e.stopPropagation();
                  if (openInNewTab) {
                    handleDismiss();
                    window.open(buttonTarget, '_blank');
                  } else {
                    handleDismiss();
                    window.open(buttonTarget, '_self');
                  }
                }}
              >
                <Text
                  style={{ color: 'white' }}
                  variant='title-xxs-500-upper'
                >
                  {buttonText}
                </Text>
              </Button>
            </div>
          </div>
        </div>
      </BaseModal>
    );
  } else {
    return (
      <BaseModal
        className={cn('BuilderCaptureModal', className)}
        onClose={handleDismiss}
      >
        <div className='BuilderCaptureModal-content'>
          <div className='BuilderCaptureModal-text-and-button-container'>
            <div className='BuilderCaptureModal-header-container'>
              {modalHeaderMobile && (
                <Text
                  variant='title-xl'
                  italic
                  style={{ color: textColor }}
                  className={cn('BuilderCaptureModal-header', 'only-mobile')}
                >
                  {modalHeaderMobile}
                </Text>
              )}
              {modalHeaderDesktop && (
                <Text
                  variant='title-xl'
                  italic
                  style={{ color: textColor }}
                  className={cn('BuilderCaptureModal-header', 'only-desktop')}
                >
                  {modalHeaderDesktop}
                </Text>
              )}
            </div>
            <div className='BuilderCaptureModal-text-container'>
              {modalTextMobile && (
                <Text
                  variant='text-m'
                  style={{ color: textColor }}
                  className={cn('BuilderCaptureModal-text', 'only-mobile')}
                >
                  {modalTextMobile}
                </Text>
              )}
              {modalTextDesktop && (
                <Text
                  variant='text-m'
                  style={{ color: textColor }}
                  className={cn('BuilderCaptureModal-text', 'only-desktop')}
                >
                  {modalTextDesktop}
                </Text>
              )}
            </div>
            <EmailSmsModalForm
              initialValues={{ email: '', phoneNumber: '', optinText: false }}
              onSubmitSuccess={() => {
                if (onFormSuccess) {
                  onFormSuccess();
                }
                setIsSubmittedSuccessfully(true);
              }}
            />
          </div>
        </div>
      </BaseModal>
    );
  }
};

export default BuilderCaptureModal;
